import { motion } from "framer-motion";
import { ArrowRight, Wrench, Shield, HeartPulse, Package, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const CustomizationCTASection = () => {
  const useCases = [
    { icon: Shield, label: "Security & Patrol" },
    { icon: HeartPulse, label: "Medical Response" },
    { icon: Package, label: "Logistics & Utility" },
    { icon: Sparkles, label: "Events & Hospitality" },
  ];

  return (
    <section className="relative overflow-hidden">
      {/* Full-width hero image background */}
      <div className="absolute inset-0">
        <img
          src="/products/itank/itank-police-patroller-hero.png"
          alt="iTank Police Patroller - Customized for Security Operations"
          className="w-full h-full object-cover object-center"
        />
        {/* Gradient overlays for readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/60" />
      </div>

      {/* Animated glow effects */}
      <motion.div
        className="absolute top-1/4 right-1/4 w-64 md:w-96 h-64 md:h-96 bg-primary/20 rounded-full blur-[100px]"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-1/4 left-1/3 w-48 md:w-64 h-48 md:h-64 bg-eco-green/20 rounded-full blur-[80px]"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="container relative mx-auto px-6 py-24 lg:py-32">
        <div className="max-w-2xl">
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Badge */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 backdrop-blur-sm border border-primary/30"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <Wrench className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">Custom Solutions</span>
            </motion.div>

            {/* Heading */}
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
              Customize Your
              <span className="block mt-2 bg-gradient-to-r from-primary via-primary to-eco-green bg-clip-text text-transparent">
                Mobility Solution
              </span>
            </h2>

            {/* Description */}
            <p className="text-xl text-muted-foreground max-w-lg leading-relaxed">
              Transform our base vehicles into purpose-built machines. From police patrol units to
              medical responders, we equip your fleet with professional-grade customizations.
            </p>

            {/* Use Cases Pills */}
            <div className="flex flex-wrap gap-3">
              {useCases.map(({ icon: Icon, label }, index) => (
                <motion.div
                  key={label}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-background/80 backdrop-blur-sm border border-border/50 hover:border-primary/50 hover:bg-background transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <Icon className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-foreground">{label}</span>
                </motion.div>
              ))}
            </div>

            {/* CTA Buttons */}
            <motion.div
              className="flex flex-wrap gap-4 pt-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.6 }}
            >
              <Button size="lg" className="group" asChild>
                <Link to="/products/customization">
                  Explore Customizations
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="bg-background/50 backdrop-blur-sm" asChild>
                <Link to="/contact?inquiry=customization">
                  Request Quote
                </Link>
              </Button>
            </motion.div>

            {/* Feature highlights */}
            <motion.div
              className="flex flex-wrap gap-4 pt-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.8 }}
            >
              <span className="px-4 py-2 text-sm font-semibold bg-background/80 backdrop-blur-sm rounded-full border border-border/50">
                🚨 Emergency Lighting
              </span>
              <span className="px-4 py-2 text-sm font-semibold bg-background/80 backdrop-blur-sm rounded-full border border-border/50">
                📢 PA System
              </span>
              <span className="px-4 py-2 text-sm font-semibold bg-background/80 backdrop-blur-sm rounded-full border border-border/50">
                💻 Rugged Tablet
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Bottom gradient line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
    </section>
  );
};

export default CustomizationCTASection;
